import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Col, Row, Space, Statistic, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  Bar, BarChart, Cell, CartesianGrid, LabelList, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import DataTable from '../components/DataTable';
import ScoreBadge from '../components/ScoreBadge';
import AiAnalysisBadge from '../components/AiAnalysisBadge';
import EmptyState from '../components/EmptyState';
import DateRangePicker, { type DateRange } from '../components/DateRangePicker';
import { dashboardApi } from '../api/dashboard.api';
import type { DashboardQuery, TopOperator } from '../types/dashboard';

// Series hue (single-series trend) + status palette for the risk breakdown (labeled, not color-alone).
const TREND_HUE = '#1677ff';
const RISK_COLORS = { low: '#52c41a', medium: '#faad14', high: '#ff4d4f' };

export default function Dashboard() {
  const [query, setQuery] = useState<DashboardQuery>({});
  const q = useQuery({ queryKey: ['dashboard', query], queryFn: () => dashboardApi.get(query) });

  const onDate = (range: DateRange) =>
    setQuery({ from: range?.[0]?.toISOString(), to: range?.[1]?.toISOString() });

  const d = q.data;
  const riskData = d
    ? [
        { name: 'کم', value: d.risk.low, color: RISK_COLORS.low },
        { name: 'متوسط', value: d.risk.medium, color: RISK_COLORS.medium },
        { name: 'زیاد', value: d.risk.high, color: RISK_COLORS.high },
      ]
    : [];

  const trendData = (d?.trend ?? []).map((t) => ({ ...t, label: dayjs(t.date).format('MM-DD') }));

  const opColumns: ColumnsType<TopOperator> = [
    { title: 'اپراتور', dataIndex: 'operatorName', key: 'operatorName' },
    { title: 'تعداد تماس', dataIndex: 'callCount', key: 'callCount', width: 120 },
    { title: <Space size={4}>میانگین امتیاز <AiAnalysisBadge compact /></Space>, key: 'avgScore', width: 160, render: (_, r) => <ScoreBadge score={r.avgScore} /> },
  ];

  return (
    <Space direction="vertical" size="large" style={{ display: 'flex' }}>
      <Card size="small">
        <Space>
          <Typography.Text type="secondary">بازه زمانی:</Typography.Text>
          <DateRangePicker onChange={onDate} />
        </Space>
      </Card>

      {d && d.kpis.totalCalls === 0 ? (
        <Card><EmptyState description="در این بازه تماسی وجود ندارد." /></Card>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {[
              { title: 'کل تماس‌ها', value: d?.kpis.totalCalls ?? 0 },
              { title: 'پردازش‌شده', value: d?.kpis.processedCalls ?? 0 },
              { title: 'میانگین امتیاز کیفیت', value: d?.kpis.avgQualityScore ?? '—', suffix: d?.kpis.avgQualityScore != null ? '/ ۱۰۰' : '', ai: true },
              { title: 'پرریسک', value: d?.kpis.highRiskCount ?? 0, ai: true },
              { title: 'نیازمند بازبینی', value: d?.kpis.reviewRequiredCount ?? 0, ai: true },
              { title: 'نرخ رفع', value: d ? Math.round(d.kpis.resolutionRate * 100) : 0, suffix: '٪', ai: true },
            ].map((k) => (
              <Col key={k.title} xs={12} md={8} lg={4}>
                <Card size="small"><Statistic title={k.ai ? <Space size={4}>{k.title}<AiAnalysisBadge compact /></Space> : k.title} value={k.value} suffix={k.suffix} loading={q.isLoading} /></Card>
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Card size="small" title="روند تماس‌ها (تعداد در روز)">
                {trendData.length > 0 ? (
                  <div style={{ direction: 'ltr' }}>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={trendData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="callCount" name="تماس‌ها" stroke={TREND_HUE} strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : <EmptyState description="داده‌ای برای روند نیست." />}
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card size="small" title={<Space size={4}>توزیع ریسک <AiAnalysisBadge compact /></Space>}>
                <div style={{ direction: 'ltr' }}>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart layout="vertical" data={riskData} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={50} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {riskData.map((r) => <Cell key={r.name} fill={r.color} />)}
                        <LabelList dataKey="value" position="right" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>
          </Row>

          <Card size="small" title="برترین اپراتورها">
            <DataTable<TopOperator> rowKey="operatorId" columns={opColumns} dataSource={d?.topOperators ?? []} loading={q.isLoading} />
          </Card>
        </>
      )}
    </Space>
  );
}
